/**
 * E-Mail-Absender (TEST-EMAIL T1b): the organisation's sender settings and their
 * change history, under Administration.
 *
 * THE PAGE OF A BACKEND THAT ALREADY DECIDES EVERYTHING (T1a). Three endpoints,
 * all `require_roles("admin")`: the read never writes, the write is partial and
 * audited, the history is the audit's read side. This page reads both on mount,
 * sends only the fields that changed, takes the server's answer as the new
 * state, and shows a refusal as the server phrased it or — where the server's
 * words are not for a reader — as a German sentence.
 *
 * ADMIN ONLY, CHECKED BEFORE ANY READ. A manager reaching the route gets the
 * refusal and no request at all, as on the Automation Center: every endpoint
 * behind it would answer 403 anyway.
 *
 * SUPPORT MODE IS THE SERVER'S CALL. The form is not locked or hidden while an
 * operator acts on behalf of an organisation: the save goes out, the server
 * refuses it with 403, and the page shows that sentence and keeps the draft.
 * Reading stays possible, and nothing is pretended to be saved.
 *
 * NO TEST SEND. The test address is configuration; sending a test mail is a
 * later slice and has no control here.
 */
import React, { useCallback } from "react";
import { toast } from "sonner";

import { useAuth } from "../../contexts/AuthContext";
import { Skeleton } from "../../components/ui/skeleton";
import { formatDateTime } from "../../utils/datetime";
import EmailSettingsActivity from "./emailSettings/EmailSettingsActivity";
import EmailSettingsForm, { FIELD_IDS } from "./emailSettings/EmailSettingsForm";
import { FIELDS, SAVE_SUCCESS } from "./emailSettings/emailSettingsModel";
import {
  useEmailSettings,
  useEmailSettingsActivity,
} from "./emailSettings/useEmailSettings";

const RETRY =
  "inline-flex h-9 items-center rounded-[9px] border border-border bg-card px-3 text-[12.5px] font-semibold " +
  "text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function PageHeader({ updatedAt = null }) {
  return (
    <header className="flex flex-col gap-1">
      <h1 className="m-0 text-[19px] font-semibold text-foreground">E-Mail-Absender</h1>
      <p className="m-0 max-w-2xl text-[12.5px] text-muted-foreground">
        Absender, Antwortadresse und Test-E-Mail-Adresse für automatisierte E-Mails
        verwalten.
      </p>
      {updatedAt ? (
        <p className="m-0 text-[11.5px] text-muted-foreground">
          Zuletzt geändert:{" "}
          <time dateTime={updatedAt} className="tabular-nums">
            {formatDateTime(updatedAt)}
          </time>
        </p>
      ) : null}
    </header>
  );
}

function FormSkeleton() {
  return (
    <div
      className="flex flex-col gap-4 rounded-[14px] border border-border bg-card p-4 md:p-5"
      aria-label="E-Mail-Einstellungen werden geladen"
      role="status"
    >
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

/** The first field in page order among `fields`, focused after a refusal. */
function focusFirst(fields) {
  const first = FIELDS.find((field) => fields.includes(field));
  if (!first) return;
  const el = document.getElementById(FIELD_IDS[first]);
  if (el) el.focus();
}

export default function AdminEmailSettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const settings = useEmailSettings({ active: isAdmin });
  const activity = useEmailSettingsActivity({ active: isAdmin });
  const { save } = settings;
  const reloadActivity = activity.reload;

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      const result = await save();
      if (result.status === "saved") {
        toast.success(SAVE_SUCCESS);
        // The save just wrote the history entry it should show — unless it
        // changed nothing, in which case the server wrote none.
        if (result.changed) reloadActivity();
      } else if (result.status === "invalid") {
        focusFirst(result.fields);
      } else if (result.status === "failed") {
        toast.error(result.message);
        focusFirst(result.fields);
      }
    },
    [save, reloadActivity]
  );

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <PageHeader />
        <p
          role="alert"
          className="m-0 rounded-[12px] border border-border bg-card px-4 py-6 text-[12.5px] text-muted-foreground"
        >
          Für die E-Mail-Absender-Einstellungen benötigst du Administratorrechte.
        </p>
      </div>
    );
  }

  let formColumn;
  if (settings.loadError) {
    formColumn = (
      <div className="flex flex-col items-start gap-2 rounded-[14px] border border-border bg-card p-4 md:p-5">
        <p
          role="alert"
          className="m-0 border-l-2 border-destructive pl-2 text-[12.5px] leading-snug text-foreground"
        >
          {settings.loadError}
        </p>
        <button type="button" onClick={settings.reload} className={RETRY}>
          Erneut versuchen
        </button>
      </div>
    );
  } else if (settings.loading) {
    formColumn = <FormSkeleton />;
  } else {
    formColumn = (
      <EmailSettingsForm
        form={settings.form}
        settings={settings.settings}
        fieldErrors={settings.fieldErrors}
        saving={settings.saving}
        dirty={settings.dirty}
        saveError={settings.saveError}
        onChange={settings.setField}
        onSubmit={handleSubmit}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      <PageHeader updatedAt={settings.settings?.updated_at || null} />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:items-start">
        {formColumn}
        <EmailSettingsActivity
          items={activity.items}
          total={activity.total}
          loading={activity.loading}
          error={activity.error}
          onRetry={activity.reload}
        />
      </div>
    </div>
  );
}
